"use client"

import "@/components/compare/compare-glass.css"

import { useEffect, useRef } from "react"
import Link from "next/link"
import { motion, MotionConfig } from "motion/react"
import {
  ArrowRight,
  BookOpen,
  Calculator,
  DollarSign,
  GitCompare,
  Leaf,
  Stethoscope,
  type LucideIcon,
} from "lucide-react"
import { AnimatedBackground } from "@/components/ui/animated-background"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { Reveal } from "@/components/motion"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { GrassVsGrassTable } from "@/components/compare/grass-vs-grass-table"
import { DecisionBlock } from "@/components/compare/decision-block"
import { ZipVerdictCallout } from "@/components/compare/zip-verdict-callout"
import {
  CompareVersusPanel,
  ComparePhotoCredits,
  type VersusSide,
} from "@/components/compare/compare-versus-panel"
import { getGrassReferencePhotoMeta } from "@/data/grass-reference-photos"
import { getComparisonIcon, getGrassIcon } from "@/components/compare/comparison-icon"
import { AppBrandTile } from "@/components/compare/app-brand-tile"
import { OwnershipDisclosure } from "@/components/compare/ownership-disclosure"
import { ReminderWidget } from "@/components/blog/reminder-widget"
import { CompareBuyPanel, type CompareBuyPanelItem } from "@/components/compare/compare-buy-panel"
import { DecisionBuyChip } from "@/components/compare/decision-buy-chip"
import { linkFromCatalog, dedupeLinks } from "@/lib/product-picks/resolve"
import { getCompareGrassProductKey, isEstablishmentPlug } from "@/data/guide-product-recommendations"
import type { ProductRecommendationLink } from "@/lib/product-picks/types"
import { ToolIllustration } from "@/components/tools/tool-illustration"
import type { ComparisonEntry, GuideSlug, TopicLinkIcon } from "@/data/comparisons"
import { trackEvent, runWhenPageVisible } from "@/lib/analytics"
import { runWhenAnalyticsReady } from "@/lib/analytics/analytics-ready"
import { useCompareZip, appendZip } from "@/hooks/use-compare-zip"

// Decision-block glyph per topic deep-link kind. Kept here so data/comparisons.tsx
// stays lucide-free and ships only a string token.
const TOPIC_LINK_ICONS: Record<TopicLinkIcon, LucideIcon> = {
  diagnose: Stethoscope,
  calculator: Calculator,
  weed: Leaf,
  guide: BookOpen,
}

// Tool calculators normalize `?grass=` by lowercasing and replacing dashes with
// spaces. Most guide slugs round-trip cleanly; St. Augustinegrass needs the
// period preserved so `st augustinegrass` matches `st. augustinegrass`.
function toToolGrassParam(slug: GuideSlug): string {
  return slug === "st-augustinegrass" ? "st.-augustinegrass" : slug
}

// Topic decision blocks route to the matching /weeds, /guides, /diagnose, or
// /tools page instead of Amazon. Labels are authored per entry and named, never
// "Learn more" (growth-design rule 2, specificity over abstraction).
function TopicSideAction({
  link,
  slug,
  side,
}: {
  link: { href: string; label: string; icon: TopicLinkIcon }
  slug: string
  side: "a" | "b"
}) {
  const LinkIcon = TOPIC_LINK_ICONS[link.icon]
  return (
    <Link
      href={link.href}
      onClick={() => trackEvent("compare_topic_link_clicked", { slug, side, href: link.href })}
    >
      <LinkIcon className="h-3.5 w-3.5" aria-hidden="true" />
      {link.label}
    </Link>
  )
}

interface RelatedComparison {
  slug: string
  shortLabel: string
  metaDescription: string
}

interface ComparisonDetailContentProps {
  entry: ComparisonEntry
  slug: string
  relatedComparisons: RelatedComparison[]
}

export function ComparisonDetailContent({
  entry,
  slug,
  relatedComparisons,
}: ComparisonDetailContentProps) {
  const viewTracked = useRef(false)
  useEffect(() => {
    if (viewTracked.current) return

    let cancelReady: (() => void) | null = null
    const cancelVisible = runWhenPageVisible(() => {
      if (viewTracked.current) return
      cancelReady = runWhenAnalyticsReady(() => {
        if (viewTracked.current) return
        viewTracked.current = true
        trackEvent('compare_viewed', { slug })
      })
    })

    return () => {
      cancelVisible()
      cancelReady?.()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { Icon } = getComparisonIcon(entry)
  const isAppMatchup = entry.kind === "brand" && entry.category === "app"

  // Page-level ZIP verdict state (inert on brand pages). Server render is
  // always the "no zip" state; every consumer below reacts post-hydration only.
  const compareZip = useCompareZip({
    enabled: entry.kind === "grass",
    grassNameA: entry.kind === "grass" ? entry.a.key : "",
    grassNameB: entry.kind === "grass" ? entry.b.key : "",
    displayNameA: entry.a.displayName,
    displayNameB: entry.b.displayName,
    slug,
  })
  const { zip, region, winnerIdx } = compareZip
  const winnerSide: "a" | "b" | null = winnerIdx === 0 ? "a" : winnerIdx === 1 ? "b" : null

  // Appends the verdict ZIP to a tool href so the calculator prefills the
  // user's region (append, don't rebuild — preserves toToolGrassParam quirks).
  const withZip = (href: string) => appendZip(href, zip)
  const trackToolLink = (tool: "fertilizer" | "cost", side: "a" | "b") => {
    trackEvent("compare_tool_link_clicked", {
      slug,
      tool,
      side,
      has_zip: Boolean(zip),
      is_winner: winnerSide === side,
    })
  }

  // --- Affiliate products per side (the /compare monetization) ---
  // Brand pages: the specific product each side names, via its catalog key.
  // Grass pages: the on-topic product a lawn owner buys to grow that grass. Seeded
  // grasses resolve their seed pick. Sod/plug-only grasses (St. Augustine) and grasses
  // the site steers away from seed (zoysia — seeded zoysia is a slow, costly specialty
  // product) carry NO seed pick on their guide, so on compare we fall back to the
  // honest establishment product (plugs) instead of leaving that side bare. This
  // fallback is compare-only; guide pages intentionally still show no seed for these.
  const grassSeed = (guideSlug: GuideSlug, displayName: string) => {
    const key = getCompareGrassProductKey(guideSlug)
    if (!key) return null
    const reason = isEstablishmentPlug(key)
      ? `How to start a ${displayName} lawn: plugs on Amazon`
      : `Top-rated ${displayName} seed on Amazon`
    return linkFromCatalog(key, reason)
  }

  // Topic pages (dormant vs dead, weed lookalikes, turf vs grass) carry no
  // affiliate surface at all: no FTC line, no buy chips, no buy panel. Their
  // conversion path is the internal deep link on each decision block instead.
  const productA: ProductRecommendationLink | null =
    entry.kind === "brand"
      ? entry.a.productKey
        ? linkFromCatalog(entry.a.productKey, entry.a.buyReason ?? "")
        : null
      : entry.kind === "grass"
        ? grassSeed(entry.a.guideSlug, entry.a.displayName)
        : null
  const productB: ProductRecommendationLink | null =
    entry.kind === "brand"
      ? entry.b.productKey
        ? linkFromCatalog(entry.b.productKey, entry.b.buyReason ?? "")
        : null
      : entry.kind === "grass"
        ? grassSeed(entry.b.guideSlug, entry.b.displayName)
        : null

  // Buy-panel items. Brand: the two products (+ badges), plus a starter-fertilizer
  // cross-sell on the seed page. Grass: each side's grass seed, deduped — a side
  // with no seed (St. Augustine) simply contributes nothing.
  const panelItems: CompareBuyPanelItem[] = []
  if (entry.kind === "brand") {
    if (productA) panelItems.push({ product: productA, badge: entry.a.badge })
    if (productB) panelItems.push({ product: productB, badge: entry.b.badge })
    if (slug === "scotts-vs-pennington-grass-seed") {
      const starter = linkFromCatalog(
        "starter-scotts",
        "Starter feed to establish new grass right after seeding."
      )
      if (starter) panelItems.push({ product: starter })
    }
  } else if (entry.kind === "grass") {
    const links = dedupeLinks(
      [productA, productB].filter(Boolean) as ProductRecommendationLink[]
    )
    for (const product of links) panelItems.push({ product })
  }
  // Grass panels usually list seed, but sod/plug grasses (St. Augustine, zoysia) list
  // plugs — so keep the heading product-neutral rather than saying "seed".
  const panelHeading =
    entry.kind === "brand" ? "Where to buy" : "Top pick to grow each grass"

  // Versus panel sides. Grass: verified reference photo (all 10 supported grasses
  // have one) + season label. Brand: name + product type + the product's own
  // packshot, so no compare page renders imageless. A side with neither (Sunday —
  // mail-order, no Amazon SKU) gets the comparison icon as a balanced fallback
  // tile. Both carry an optional per-side buy chip (productA/B).
  const versusA: VersusSide =
    entry.kind === "grass"
      ? {
          name: entry.a.displayName,
          sub: getGrassIcon(entry.a.key).seasonLabel,
          photo: getGrassReferencePhotoMeta(entry.a.key),
          // Grasses without a verified reference photo (annual rye, the
          // cool-season blend — see data/grass-reference-photos.ts TODO) fall
          // back to their seed packshot so the VS split stays balanced.
          productImage: productA?.imageUrl ?? null,
          buy: productA,
        }
      : entry.kind === "topic"
        ? {
            name: entry.a.displayName,
            sub: entry.a.sub,
            photo: entry.a.photo ?? null,
            photoAlt: entry.a.photo?.caption ?? undefined,
            fallbackIcon: <Icon className="cmp-versus__fallback-ic" aria-hidden="true" />,
          }
        : {
            name: entry.a.displayName,
            sub: entry.a.productType,
            productImage: productA?.imageUrl ?? null,
            tile: entry.a.appTile ? (
              <AppBrandTile tile={entry.a.appTile} name={entry.a.displayName} variant="panel" />
            ) : undefined,
            fallbackIcon: <Icon className="cmp-versus__fallback-ic" aria-hidden="true" />,
            buy: productA,
          }
  const versusB: VersusSide =
    entry.kind === "grass"
      ? {
          name: entry.b.displayName,
          sub: getGrassIcon(entry.b.key).seasonLabel,
          photo: getGrassReferencePhotoMeta(entry.b.key),
          productImage: productB?.imageUrl ?? null,
          buy: productB,
        }
      : entry.kind === "topic"
        ? {
            name: entry.b.displayName,
            sub: entry.b.sub,
            photo: entry.b.photo ?? null,
            photoAlt: entry.b.photo?.caption ?? undefined,
            fallbackIcon: <Icon className="cmp-versus__fallback-ic" aria-hidden="true" />,
          }
        : {
            name: entry.b.displayName,
            sub: entry.b.productType,
            productImage: productB?.imageUrl ?? null,
            tile: entry.b.appTile ? (
              <AppBrandTile tile={entry.b.appTile} name={entry.b.displayName} variant="panel" />
            ) : undefined,
            fallbackIcon: <Icon className="cmp-versus__fallback-ic" aria-hidden="true" />,
            buy: productB,
          }

  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen relative">
        <AnimatedBackground />
        <div className="relative">
          <article className="cmp-root pt-6 pb-20">
            <div className="cmp-wrap">
              {/* Decorative only: page.tsx emits the BreadcrumbList JSON-LD. */}
              <BreadcrumbComponent
                emitSchema={false}
                items={[
                  { label: "Home", href: "/" },
                  { label: "Compare", href: "/compare" },
                  { label: entry.shortLabel },
                ]}
              />

              {/* Header: centered pill + balanced display-serif title */}
              <motion.header {...FADE_UP} transition={FADE_TRANSITION} className="cmp-dhead">
                <span className="cmp-pill">
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  Side-by-side decision guide
                </span>
                <h1>{entry.h1}</h1>
                <p className="mt-2 text-center text-[12px] text-slate-500">
                  Updated{" "}
                  {new Date(`${entry.lastModified}T12:00:00Z`).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    timeZone: "UTC",
                  })}
                </p>
              </motion.header>

              {/* GEO direct answer, then the ownership note when we are one of
                  the two sides. Both sit above the VS panel so the answer and
                  the conflict of interest are the first two things read. Both
                  are null-guarded, so entries without them render unchanged. */}
              {entry.directAnswer && <p className="cmp-lede">{entry.directAnswer}</p>}
              {entry.ownershipDisclosure && (
                <OwnershipDisclosure text={entry.ownershipDisclosure} className="mt-4" />
              )}

              {/* Signature VS panel — verified grass photos (or brand text) */}
              <motion.div {...FADE_UP} transition={FADE_TRANSITION}>
                <CompareVersusPanel a={versusA} b={versusB} slug={slug} />
              </motion.div>

              {/* FTC disclosure directly under the hero card, since the VS panel now
                  carries "View on Amazon" affiliate chips. Only shown when a chip
                  renders (i.e. at least one side has an Amazon product). */}
              {(productA || productB) && (
                <p className="mx-auto mt-3 max-w-[640px] px-4 text-center text-[11px] text-slate-500">
                  As an Amazon Associate we earn from qualifying purchases.{" "}
                  <Link
                    href="/legal#affiliate-disclaimer"
                    className="text-inherit no-underline hover:underline"
                  >
                    Disclosure
                  </Link>
                  .
                </p>
              )}

              <Reveal as="div" className="cmp-prose">
                {entry.intro}
              </Reveal>

              <Reveal as="div" className="cmp-verdict">
                <p className="cmp-verdict__label">Quick verdict</p>
                <p>{entry.verdict}</p>
              </Reveal>

              {entry.kind === "grass" && (
                <Reveal as="div" className="cmp-block">
                  <ZipVerdictCallout
                    zip={compareZip.zip}
                    region={compareZip.region}
                    totals={compareZip.totals}
                    displayNameA={entry.a.displayName}
                    displayNameB={entry.b.displayName}
                    prefillSource={compareZip.prefillSource}
                    onSubmit={compareZip.submitZip}
                  />
                </Reveal>
              )}

              <Reveal as="section" className="cmp-block">
                <h2 className="cmp-block__h">
                  {entry.a.displayName} vs {entry.b.displayName}: at a glance
                </h2>
                <GrassVsGrassTable
                  aLabel={entry.a.displayName}
                  bLabel={entry.b.displayName}
                  rows={entry.table}
                  winner={winnerSide}
                  winnerBadgeLabel={region ? `Best for ${region}` : undefined}
                />
              </Reveal>

              <Reveal as="section" className="cmp-block">
                <DecisionBlock
                  headline={entry.pickA.headline}
                  reasons={entry.pickA.reasons}
                  variant="a"
                  actions={
                    <>
                      {productA && <DecisionBuyChip product={productA} slug={slug} variant="a" />}
                      {entry.kind === "topic" && entry.a.link && (
                        <TopicSideAction link={entry.a.link} slug={slug} side="a" />
                      )}
                      {entry.kind === "grass" && (
                        <>
                          <Link
                            href={withZip(`/tools/fertilizer-calculator?grass=${toToolGrassParam(entry.a.guideSlug)}`)}
                            onClick={() => trackToolLink("fertilizer", "a")}
                          >
                            <Calculator className="h-3.5 w-3.5" aria-hidden="true" />
                            Fertilizer for {entry.a.displayName}
                          </Link>
                          <Link
                            href={withZip(`/tools/lawn-cost-estimator?grass=${toToolGrassParam(entry.a.guideSlug)}`)}
                            onClick={() => trackToolLink("cost", "a")}
                          >
                            <DollarSign className="h-3.5 w-3.5" aria-hidden="true" />
                            {winnerSide === "a" && region
                              ? `Estimate ${entry.a.displayName} cost in ${region}`
                              : "Estimate cost"}
                          </Link>
                        </>
                      )}
                    </>
                  }
                />
                <DecisionBlock
                  headline={entry.pickB.headline}
                  reasons={entry.pickB.reasons}
                  variant="b"
                  actions={
                    <>
                      {productB && <DecisionBuyChip product={productB} slug={slug} variant="b" />}
                      {entry.kind === "topic" && entry.b.link && (
                        <TopicSideAction link={entry.b.link} slug={slug} side="b" />
                      )}
                      {entry.kind === "grass" && (
                        <>
                          <Link
                            href={withZip(`/tools/fertilizer-calculator?grass=${toToolGrassParam(entry.b.guideSlug)}`)}
                            onClick={() => trackToolLink("fertilizer", "b")}
                          >
                            <Calculator className="h-3.5 w-3.5" aria-hidden="true" />
                            Fertilizer for {entry.b.displayName}
                          </Link>
                          <Link
                            href={withZip(`/tools/lawn-cost-estimator?grass=${toToolGrassParam(entry.b.guideSlug)}`)}
                            onClick={() => trackToolLink("cost", "b")}
                          >
                            <DollarSign className="h-3.5 w-3.5" aria-hidden="true" />
                            {winnerSide === "b" && region
                              ? `Estimate ${entry.b.displayName} cost in ${region}`
                              : "Estimate cost"}
                          </Link>
                        </>
                      )}
                    </>
                  }
                />
              </Reveal>

              {/* Topic pages have no buy panel, so the tool CTA takes the same
                  post-decision slot: the reader has just picked a side and the
                  next action is a calculator, not a purchase. Artwork is the
                  route's bespoke ToolIllustration SVG, never a lucide glyph. */}
              {entry.kind === "topic" && entry.toolCta && (
                <Reveal as="section" className="cmp-block">
                  <Link
                    href={entry.toolCta.href}
                    className="cmp-gcard"
                    onClick={() =>
                      trackEvent("compare_tool_link_clicked", {
                        slug,
                        tool: entry.toolCta!.href,
                        side: "cta",
                      })
                    }
                  >
                    <div className="flex items-start gap-3">
                      <span className="shrink-0" aria-hidden="true">
                        <ToolIllustration href={entry.toolCta.href} />
                      </span>
                      <div>
                        <div className="cmp-gcard__name">{entry.toolCta.label}</div>
                        <p className="mt-1 text-[13px] leading-relaxed text-slate-600">
                          {entry.toolCta.blurb}
                        </p>
                      </div>
                    </div>
                    <span className="cmp-gcard__go">
                      Open the tool
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                  </Link>
                </Reveal>
              )}

              {/* Affiliate buy panel — the /compare monetization, placed at the
                  decision moment (2026-07-15: moved up from below FAQs/related,
                  where it earned 0 clicks on 55 impressions/wk — the reader who
                  just picked a side is the buyer; peak-end rule). Intentional
                  two-surface design (top VS chips + decision-block chips + this
                  richer card panel); each is a distinct section, so this does not
                  violate the "one product display per section" rule. */}
              {panelItems.length > 0 && (
                <Reveal as="section" className="cmp-block">
                  <CompareBuyPanel heading={panelHeading} items={panelItems} slug={slug} />
                  {entry.kind === "brand" &&
                    (entry.a.productsCategory || entry.b.productsCategory) && (
                      <p className="mt-3 text-[13px] text-slate-500">
                        Prefer other options? Browse{" "}
                        {entry.a.productsCategory && (
                          <Link
                            className="underline hover:text-slate-700"
                            href={`/products?category=${encodeURIComponent(entry.a.productsCategory)}&source=compare-${slug}`}
                          >
                            {entry.a.productType.toLowerCase()}
                          </Link>
                        )}
                        {entry.a.productsCategory && entry.b.productsCategory && " and "}
                        {entry.b.productsCategory && (
                          <Link
                            className="underline hover:text-slate-700"
                            href={`/products?category=${encodeURIComponent(entry.b.productsCategory)}&source=compare-${slug}`}
                          >
                            {entry.b.productType.toLowerCase()}
                          </Link>
                        )}{" "}
                        in our catalog.
                      </p>
                    )}
                </Reveal>
              )}

              {/* Post-decision capture (peak-end + specificity from the
                  growth-design playbook): the reader has just absorbed the
                  verdict + "pick A/B if" logic — the engaged-reader peak — so
                  ask here, not at the cold hero. shortLabel ("Bermuda vs Zoysia")
                  drives matchup-specific copy via the component's raw-category
                  fallback; `compare-read` segments these captures in PostHog. */}
              <Reveal as="div" className="cmp-block">
                <ReminderWidget
                  variant="seasonal"
                  source="compare-read"
                  placement="compare-read"
                  slug={slug}
                />
              </Reveal>

              <Reveal as="section" className="cmp-block">
                <h2 className="cmp-block__h">Frequently asked questions</h2>
                <div className="cmp-faqlist">
                  {entry.faqs.map((faq) => (
                    <details key={faq.question} className="cmp-faqi">
                      <summary className="cmp-faqi__q">
                        {faq.question}
                        <span className="cmp-faqi__ic" aria-hidden="true">+</span>
                      </summary>
                      <p className="cmp-faqi__a">{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </Reveal>

              {(relatedComparisons.length > 0 || isAppMatchup) && (
                <Reveal as="section" className="cmp-block">
                  <h2 className="cmp-block__h cmp-block__h--meta">More comparisons</h2>
                  <div className="cmp-related">
                    {relatedComparisons.map((related) => (
                      <Link key={related.slug} href={`/compare/${related.slug}`} className="cmp-rel">
                        <GitCompare className="h-3.5 w-3.5" aria-hidden="true" />
                        {related.shortLabel}
                        <ArrowRight className="h-3 w-3" aria-hidden="true" />
                      </Link>
                    ))}
                    {/* App matchups have exactly one sibling page, so the roundup
                        joins them here rather than leaving a single lonely pill. */}
                    {isAppMatchup && (
                      <Link href="/best-grass-identification-app" className="cmp-rel">
                        <GitCompare className="h-3.5 w-3.5" aria-hidden="true" />
                        Every grass identification app, compared
                        <ArrowRight className="h-3 w-3" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                </Reveal>
              )}

              {entry.kind === "grass" && (
                <Reveal as="section" className="cmp-block">
                  <h2 className="cmp-block__h">Want the full grass guide?</h2>
                  <div className="cmp-guides">
                    {[entry.a, entry.b].map((side) => {
                      const { seasonLabel } = getGrassIcon(side.key)
                      return (
                        <Link key={side.guideSlug} href={`/guides/${side.guideSlug}`} className="cmp-gcard">
                          <div>
                            <div className="cmp-gcard__name">{side.displayName}</div>
                            {seasonLabel && <div className="cmp-gcard__sub">{seasonLabel}</div>}
                          </div>
                          <span className="cmp-gcard__go">
                            Read the {side.displayName} guide
                            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                          </span>
                        </Link>
                      )
                    })}
                  </div>
                </Reveal>
              )}
            </div>
          </article>
        </div>
      </div>
      {/* Photo credits render below the site footer, never near the
          VS panel. */}
      <ComparePhotoCredits
        photos={[
          { name: entry.a.displayName, photo: versusA.photo },
          { name: entry.b.displayName, photo: versusB.photo },
        ]}
      />
    </MotionConfig>
  )
}
